import { AUTH, UBI_CONNECT } from '../utils/constants';
import { RootState, AuthState as State } from '../models/state';

import { combineActions, createAction, handleActions } from 'redux-actions';

import { UbisoftSSO } from '../models/ubisoft';

import { assign } from 'lodash';
import { createLoadingSelector } from '../utils/selectors';
import { getProfile } from '../services/ubisoftServices';
import { success } from '../utils/reducer';

let SDK: any;

const initializeConnect = () =>
  new Promise<State>((resolve, reject) => {
    Connect.init({
      env: UBI_CONNECT.env,
      appId: UBI_CONNECT.appId,
      genomeId: UBI_CONNECT.genomeId,
      lang: 'en-US',
      nextUrl: encodeURIComponent(UBI_CONNECT.nextURL),
      thirdPartyCookiesSupport: false
    });

    Connect.sdk.subscribe(async (connectSDK: any) => {
      // Save the sdk for later reuse
      SDK = connectSDK;

      try {
        const auth = await validateTicket();
        assign(AUTH, auth);

        const [profile] = await Promise.allSettled([getProfile(auth.userId)]);
        resolve({
          ...auth,
          ...(profile.status === 'fulfilled' && profile.value),
          cancelLogIn: false
        });
      } catch (error) {
        reject(error);
      }
    });
  });

// Will check if the ticket is still valid
// The retry is to attempt fixing a ubisoft connect
// issue that sometimes returns an empty payload
const validateTicket = () =>
  new Promise<UbisoftSSO>((resolve, reject) => {
    let count = 0;

    const recursiveCheck = () => {
      SDK.getTicket().subscribe((response: any) => {
        if (response.status !== 'ok') return reject(response);
        if (!response.payload || !response.payload.userId) {
          return ++count < 2 ? recursiveCheck() : reject(response);
        }
        resolve(response.payload);
      });
    };

    recursiveCheck();
  });

const logout = () =>
  validateTicket()
    .then((res) =>
      SDK.logout(res.ticket, res.sessionId).subscribe(() => {
        const path =
          window.location.pathname.indexOf(`/profile`) > -1
            ? window.location.pathname.substring(0, 7)
            : window.location.pathname;
        return (window.location.pathname = path);
      })
    )
    // If it fails, the user is already logged out, hence the reload
    .catch(() => document.location.reload());

// Actions
export const LOG_IN = 'auth/LOG_IN';
export const LOG_OUT = 'auth/LOG_OUT';
export const CANCEL_LOG_IN = 'auth/CANCEL_LOG_IN';

// Action creators
export const logIn = createAction(LOG_IN, initializeConnect);
export const logOut = createAction(LOG_OUT, logout);
export const cancelLogIn = createAction(CANCEL_LOG_IN);

// Default state
export const defaultState: any = {
  cancelLogIn: false
};

// Reducer
const reducer = handleActions(
  {
    [combineActions(...success([LOG_IN])) as any]: (
      state: State,
      { payload }
    ) => ({
      ...state,
      ...payload
    }),
    [LOG_OUT]: () => ({ ...defaultState }),
    [CANCEL_LOG_IN]: (state: State) => ({
      ...state,
      cancelLogIn: true
    })
  },
  defaultState
);

// Selectors
const getLoggingLoading = createLoadingSelector([LOG_IN]);

export const selectAuth = (state: RootState) => state.auth;
export const selectIsLoggingIn = (state: RootState) => getLoggingLoading(state);
export const selectIsCancelLogin = (state: RootState) => state.auth.cancelLogIn;

export default reducer;
